import { type QueryClient } from '@tanstack/react-query';
import { ATTENDANCE_MODES, CHECK_IN_ERROR_CODES } from '../constants/schedule.constants';
import { extractSession } from '../utils/check-in-session';
import { getNextCheckInOpen } from '../utils/check-in-window';
import type { CheckInTimingEvent } from '../utils/check-in-window';
import { scheduleEventKeys } from './schedule-events.hooks';

export type AttendanceMode = (typeof ATTENDANCE_MODES)[keyof typeof ATTENDANCE_MODES];

export interface CheckInItem {
  id: string;
  userId: string;
  checkedInAt?: string;
  method?: string;
  attendanceMode?: AttendanceMode;
  [key: string]: unknown;
}

export interface CheckInsPage {
  items: CheckInItem[];
  total: number;
  cursor: string | null; // null = last page
}

export interface CheckInFilters {
  attendanceMode?: AttendanceMode;
  method?: string;
  userId?: string;
}

export interface MyCheckIn {
  checkedIn: boolean;
  checkIn?: {
    id: string;
    checkedInAt?: string;
    method?: string;
    attendanceMode?: AttendanceMode;
  } | null;
}

export interface CheckInSession {
  /** null for a STATIC-QR event opened by its scheduled window. */
  totpSecret: string | null;
  step: number;
  openedAt?: string;
  /** Next scheduled auto-close; null when the session stays open until closed by hand. */
  closesAt: string | null;
}

/** Backend-resolved check-in state on event responses (detail, list, create, patch). */
export interface CheckInStatus {
  isOpen: boolean;
  /** Next scheduled open; null when open now or nothing is scheduled. */
  opensAt: string | null;
  /** Next scheduled close; null when open until closed by hand or nothing is scheduled. */
  closesAt: string | null;
}

export interface CloseSummary {
  openedAt?: string;
  closedAt?: string;
  totalCheckIns?: number;
  inPersonCount?: number;
  virtualCount?: number;
}

export type OpenResult = { ok: true } | { ok: false; error: unknown };

export type CloseResult =
  | { ok: true; alreadyClosed?: false; summary: CloseSummary }
  | { ok: true; alreadyClosed: true }
  | { ok: false; error: unknown };

export interface SessionOptions {
  enabled?: boolean;
  onError?: (error: unknown, context: Record<string, unknown>) => void;
}

export const DEFAULT_CHECK_INS_LIMIT = 500;
export const DEFAULT_POLL_INTERVAL_MS = 5000;
export const CHECK_INS_POLL_INTERVAL_MS = 10000;
export const MAX_BACKOFF_MS = 30000;

export const CHECK_IN_SESSION_STATUS = {
  IDLE: 'idle',
  OPEN: 'open',
  ERROR: 'error',
} as const;

export type CheckInSessionStatus =
  (typeof CHECK_IN_SESSION_STATUS)[keyof typeof CHECK_IN_SESSION_STATUS];

/**
 * Result variants surfaced by useQrCheckIn / useSelfAttestation — maps onto the
 * unified result screen. `TOO_EARLY` and `CAMERA_DENIED` are never returned by
 * the mutations: the client derives them (see `refineClosedResult`, and the
 * scanner's permission state).
 */
export const CHECK_IN_RESULT = {
  SUCCESS: 'success',
  ALREADY_CHECKED_IN: 'already_checked_in',
  INVALID_CODE: 'invalid_code',
  SESSION_CLOSED: 'session_closed',
  TOO_EARLY: 'too_early',
  CAMERA_DENIED: 'camera_denied',
  DISABLED: 'disabled',
  FAILED: 'failed',
} as const;

export type CheckInResult = (typeof CHECK_IN_RESULT)[keyof typeof CHECK_IN_RESULT];

export const attendanceKeys = {
  all: ['attendance'] as const,
  myCheckIn: (eventId: string | null | undefined) =>
    [...attendanceKeys.all, 'my-check-in', eventId] as const,
  session: (eventId: string | null | undefined) =>
    [...attendanceKeys.all, 'session', eventId] as const,
  checkIns: (eventId: string | null | undefined, params?: { limit: number } & CheckInFilters) =>
    [...attendanceKeys.all, 'check-ins', eventId, ...(params ? [params] : [])] as const,
  checkedInCount: (eventId: string | null | undefined) =>
    [...attendanceKeys.all, 'checked-in-count', eventId] as const,
};

/**
 * Poll interval for the checked-in count: base `intervalMs` while healthy, then
 * exponential backoff (2x per consecutive failure) capped at 30s.
 */
export const checkedInCountInterval = (intervalMs: number, failureCount: number): number =>
  Math.min(intervalMs * 2 ** failureCount, MAX_BACKOFF_MS);

export const invalidateEventAttendance = (qc: QueryClient, eventId: string) => {
  qc.invalidateQueries({ queryKey: attendanceKeys.myCheckIn(eventId) });
  qc.invalidateQueries({ queryKey: attendanceKeys.checkIns(eventId) });
  qc.invalidateQueries({ queryKey: attendanceKeys.checkedInCount(eventId) });
  qc.invalidateQueries({ queryKey: scheduleEventKeys.detail(eventId) });
};

/**
 * The session GET only answers 200 while check-in is open, and a window-opened
 * STATIC session may carry no `openedAt` — so any non-closed payload is open.
 */
export const readOpenSession = (
  data: {
    openedAt?: string;
    closedAt?: string | null;
    closesAt?: string | null;
  } | null,
): CheckInSession | null => (data && !data.closedAt ? extractSession(data) : null);

// null → undefined: react-query treats undefined as "no next page".
export const nextCheckInsCursor = (lastPage: CheckInsPage): string | undefined =>
  lastPage.cursor ?? undefined;

const checkInErrorCode = (error: unknown): string | undefined =>
  (error as { response?: { data?: { code?: string } } } | undefined)?.response?.data?.code;

export const isExpectedQrError = (error: unknown): boolean =>
  Object.values(CHECK_IN_ERROR_CODES).includes(checkInErrorCode(error) as never);

export const mapQrError = (error: unknown): CheckInResult => {
  const errorCode = checkInErrorCode(error);
  switch (errorCode) {
    case CHECK_IN_ERROR_CODES.SESSION_CLOSED:
      return CHECK_IN_RESULT.SESSION_CLOSED;
    case CHECK_IN_ERROR_CODES.ALREADY_CHECKED_IN:
      return CHECK_IN_RESULT.ALREADY_CHECKED_IN;
    case CHECK_IN_ERROR_CODES.INVALID_CODE:
      return CHECK_IN_RESULT.INVALID_CODE;
    default:
      return CHECK_IN_RESULT.INVALID_CODE;
  }
};

const SELF_ATTEST_ERROR_CODES: readonly string[] = [
  CHECK_IN_ERROR_CODES.SESSION_CLOSED,
  CHECK_IN_ERROR_CODES.SELF_ATTESTATION_DISABLED,
  CHECK_IN_ERROR_CODES.ALREADY_CHECKED_IN,
];

export const isExpectedSelfAttestError = (error: unknown): boolean =>
  SELF_ATTEST_ERROR_CODES.includes(checkInErrorCode(error) as never);

export const mapSelfAttestError = (error: unknown): CheckInResult => {
  const errorCode = checkInErrorCode(error);
  switch (errorCode) {
    case CHECK_IN_ERROR_CODES.SESSION_CLOSED:
      return CHECK_IN_RESULT.SESSION_CLOSED;
    case CHECK_IN_ERROR_CODES.SELF_ATTESTATION_DISABLED:
      return CHECK_IN_RESULT.DISABLED;
    case CHECK_IN_ERROR_CODES.ALREADY_CHECKED_IN:
      return CHECK_IN_RESULT.ALREADY_CHECKED_IN;
    default:
      return CHECK_IN_RESULT.FAILED;
  }
};

/**
 * Whether a result means the attendee is checked in, so the event's attendance
 * caches are stale: a fresh check-in, or one the backend already had.
 *
 * @param result - A `CHECK_IN_RESULT` value.
 * @returns True for `SUCCESS` and `ALREADY_CHECKED_IN`.
 * @example
 * if (isCheckedInResult(result)) invalidateEventAttendance(qc, eventId);
 */
export const isCheckedInResult = (result: CheckInResult): boolean =>
  result === CHECK_IN_RESULT.SUCCESS || result === CHECK_IN_RESULT.ALREADY_CHECKED_IN;

/**
 * Splits the backend's `session_closed` into "not open yet" and "closed": the
 * backend answers both with the same code, and only the check-in window tells
 * them apart. Any other result passes through unchanged.
 *
 * @param result - The result a check-in attempt produced.
 * @param event - The event, with its backend `checkIn` state and window attributes.
 * @param now - The current time in ms. Default `Date.now()`.
 * @returns `TOO_EARLY` when check-in opens later, otherwise `result`.
 * @example
 * navigation.navigate(ROUTES.CHECK_IN_RESULT, { event, result: refineClosedResult(result, event) });
 */
export const refineClosedResult = (
  result: CheckInResult,
  event: CheckInTimingEvent | null | undefined,
  now: number = Date.now(),
): CheckInResult =>
  result === CHECK_IN_RESULT.SESSION_CLOSED && getNextCheckInOpen({ event, now }) !== null
    ? CHECK_IN_RESULT.TOO_EARLY
    : result;
